import { createContext, useContext, useState } from 'react'

import { Nullable } from '../../utils'
import { CpfValidation_ViewerFragment } from './__generated__/cpf-validation.graphql'
import { Steps } from './enums'
import { CpfValidationEventData, CpfValidationTexts } from './types'

export interface ModalContextProps {
  setCurrentStep: (currentStep: Steps) => void
  children: React.ReactNode
  replacementToken: Nullable<string>
  eventData: CpfValidationEventData
  viewer: CpfValidation_ViewerFragment | null
  validationOrigin: string
  texts?: CpfValidationTexts
  onAfterValidationSuccess?: () => void | Promise<void>
}

export interface SharedDataInterface {
  cpf?: string
  birthDate?: Date
  profileInUse?: {
    pid: number
    email: string
  }
}

export interface ContextDataInterface {
  setCurrentStep: (currentStep: Steps) => void
  sharedData: SharedDataInterface
  setSharedData: (data: Record<string, unknown>) => void
  replacementToken: Nullable<string>
  eventData: CpfValidationEventData
  viewer: CpfValidation_ViewerFragment | null
  validationOrigin: string
  texts?: CpfValidationTexts
  onAfterValidationSuccess?: () => void | Promise<void>
}

const ModalContext = createContext<Nullable<ContextDataInterface>>(null)

export const useModalContext = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModalContext must be used within a ModalContextProvider')
  }
  return context
}

export function ModalContextProvider(props: ModalContextProps) {
  const {
    setCurrentStep,
    children,
    replacementToken,
    viewer,
    eventData,
    validationOrigin,
    texts,
    onAfterValidationSuccess,
  } = props
  const [sharedData, setSharedData] = useState<SharedDataInterface>({})

  const contextData = {
    setCurrentStep,
    sharedData,
    setSharedData,
    replacementToken,
    viewer,
    eventData,
    validationOrigin,
    texts,
    onAfterValidationSuccess,
  }

  return <ModalContext.Provider value={contextData}>{children}</ModalContext.Provider>
}
